import React from 'react'
import { AppWindow, PanelRight, SquareDashed } from 'lucide-react'
import { SvgIcon, SvgIconType } from '@/components/svg-icon'
import { mergeClasses } from '@/helpers/styling'
import { SimpleTooltip } from '@/components/ui-primitives/simple-tooltip'

/** Where a widget's editor is shown: on top of the widget, in the side panel, or in a floating window */
export type EditorSize = "overlay" | "docked" | "floating"

type SizeOption = { size: EditorSize, label: string, Icon: SvgIconType }

export const EDITOR_SIZES: SizeOption[] = [
  { size: "overlay", label: "On the widget", Icon: SquareDashed },
  { size: "docked", label: "In the side panel", Icon: PanelRight },
  { size: "floating", label: "Floating window", Icon: AppWindow },
]

/** The same three sizes for the widget's settings and for its query */
export function SizeSwitch(props: {
  value: EditorSize
  onChange: (size: EditorSize) => void
  /** Leaves out the sizes that make no sense where the editor is */
  sizes?: EditorSize[]
  /** Renames a size where it means something more specific */
  labels?: Partial<Record<EditorSize, Omit<SizeOption, "size">>>
}) {
  const options = EDITOR_SIZES
    .filter(({ size }) => !props.sizes || props.sizes.includes(size))
    .map(option => ({ ...option, ...props.labels?.[option.size] }))
  return (
    <div role="group" aria-label="Size" className="flex shrink-0 overflow-hidden rounded-md border-[1px] border-border">
      {options.map(({ size, label, Icon }) => (
        <SimpleTooltip key={size} tooltip={label}>
          <button type="button"
                  aria-label={label}
                  aria-pressed={size === props.value}
                  onClick={() => size !== props.value && props.onChange(size)}
                  className={mergeClasses(`
                    flex h-[26px] w-7 items-center justify-center border-l-[1px] border-border
                    text-muted-foreground first:border-l-0 hover:text-foreground`, {
                    "bg-[#51479e]/10 text-[#51479e] hover:text-[#51479e]": size === props.value,
                  })}>
            <SvgIcon icon={Icon} size={13} />
          </button>
        </SimpleTooltip>
      ))}
    </div>
  )
}
